/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
// import { A } from '@ember/array';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { queryManager } from 'ember-apollo-client';
import { QueryManager } from '@apollo/client/core/QueryManager';
import bulkInviteTeacherToSchool from 'dashboard/gql/mutations/bulkInviteTeacherToSchool.gql';
import Route from '@ember/routing/route';
import type { IntlService } from 'ember-intl';
import AnalyticsService from 'dashboard/services/analytics';
import { isValidEmailAddress } from '@showbie/utilities';
import type ToastService from 'dashboard/services/toast';
import type {
  AnalyticsMetadata,
  School,
  InstitutionType,
} from 'dashboard/types';

interface TeacherInviteSignature {
  Element: HTMLDivElement;
  Args: {
    closeModal(): void;
    currentSchool?: School;
    schools: {
      id: string;
      district: string;
      name: string;
      type: InstitutionType;
      children?: School[];
    }[];
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    TeacherInviteComponent: typeof TeacherInviteComponent;
    'Modals::TeacherInvite': typeof TeacherInviteComponent;
  }
}

import type { ChipToken } from 'dashboard/components/input-chips';

export default class TeacherInviteComponent extends Component<TeacherInviteSignature> {
  @queryManager declare apollo: QueryManager<Route>;
  @service declare analytics: AnalyticsService;
  @service declare intl: IntlService;
  @service declare toast: ToastService;

  /** ID of the selected school; only applies if the current purchaser is a district. */
  @tracked selectedSchoolId: string;

  /** Array of all user inputted email addresses. Mapped to the InputChips component. */
  @tracked allEmails: ChipToken[] = [];

  get validEmails(): string[] {
    return this.allEmails.filter((x) => x.valid).map((x) => x.text);
  }

  get invalidEmails(): string[] {
    return this.allEmails.filter((x) => !x.valid).map((x) => x.text);
  }

  /** Error message to display to user when something has gone wrong with the invite. */
  @tracked inviteError = '';

  /**
   * A list a relevant schools given the current context.
   * If the currently selected context is a district, all the child schools
   * from that district are returned. Otherwise, the single relevant school
   * is returned on its own.
   */
  get relevantSchools(): School[] {
    if (!this.args.currentSchool) return [];
    const { type, id } = this.args.currentSchool;
    return type === 'District'
      ? this.args.schools.filter((child) => child.district === id)
      : [this.args.currentSchool];
  }

  /**
   * Checks if the user is in a district view and has not yet selected a school.
   */
  get mustChooseSchool(): boolean {
    return this.relevantSchools.length > 1 && !this.selectedSchoolId;
  }

  /**
   * Confirms the requirements before the form can be submitted.
   * All the following conditions must be met:
   * - at least one valid email address exists
   * - zero invalid emails exist
   * - if district, a school has been selected
   */
  get canSubmitForm() {
    return (
      this.validEmails.length > 0 &&
      this.invalidEmails.length === 0 &&
      !this.mustChooseSchool
    );
  }

  get disableForm() {
    return !this.canSubmitForm;
  }

  /**
   * Invite Teacher function checks input for unsubmitted strings,
   * valid email array mutation, triggers notification on success.
   */
  @action async inviteTeachers(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (this.disableForm || !this.args.currentSchool) return;

    let outgoingSchoolId: string = this.args.currentSchool.id;

    if (this.args.currentSchool.type === 'District') {
      outgoingSchoolId = this.selectedSchoolId;
    }

    try {
      await this.apollo.mutate({
        mutation: bulkInviteTeacherToSchool,
        variables: {
          emails: this.validEmails,
          schoolId: outgoingSchoolId,
        },
      });
    } catch (reason) {
      // @ts-ignore
      const error = reason.errors?.[0];
      if (error?.extensions?.code === '51013') {
        this.inviteError = 'One or more email addresses is already approved.';
      } else {
        throw reason;
      }
      // abort invite flow
      return;
    }

    this.trackMetrics();

    const notifyString = this.intl.t('{count} invites sent!', {
      count: this.validEmails.length,
    });
    this.toast.notify(notifyString, 'success');
    // dynamic id to update describeby in dashboard
    this.args.closeModal?.();
  }

  @action handleEmailUpdate(items: ChipToken[]) {
    // update local list of email addresses
    this.allEmails = [...items];
    // reset any previous service errors
    this.inviteError = '';
  }

  @action validateIndividualEmail(emailString: string): boolean {
    return isValidEmailAddress(emailString);
  }

  @action serializeEmailInput(value: string): string[] {
    console.log('serializeEmailInput', value);
    const prep = value.split(/,|\s|;\s|:\s/).filter(Boolean);
    return prep.map((email) => {
      // trim any trailing `;:` characters
      // these can sneak by the regex above if there's only a single email address
      // or on the last email address in a list that is pasted in.
      email = email.trim().replace(/:|;$/, '');

      // pluck the email address from between angle brackets <foo@email.com>
      const angleBracketRegexArray = /^<(.*)>$/.exec(email);
      if (angleBracketRegexArray) {
        email = angleBracketRegexArray[1] ?? email;
      }
      return email;
    });
  }

  @action handleSchoolChange(id: string) {
    this.selectedSchoolId = id;
  }

  /**
   * Track metrics on completion of invite a teacher flow.
   */
  trackMetrics() {
    const metadata: AnalyticsMetadata = {
      count: this.validEmails.length as number,
    };
    this.analytics.track('app__invite_teachers', metadata);
  }
}
